/**
 * Layout wrapper for onboarding with sidebar navigation
 * Provides the layout structure for the onboarding experience
 */

import React, { useState } from 'react';
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from '@/components/ui/sidebar';
import { Separator } from '@/components/ui/separator';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';

import OnboardingSidebar from './OnboardingSidebar';
import { OnboardingSection } from '@/types/onboarding';

interface OnboardingLayoutProps {
  children: React.ReactNode;
  activeSection: string;
  onSectionChange: (section: string) => void;
}

// Section title mapping for breadcrumb
const SECTION_TITLES = {
  [OnboardingSection.BUSINESS_DETAILS]: 'Business Details',
  [OnboardingSection.STRIPE_CONNECT]: 'Payment Setup',
  [OnboardingSection.DOMAIN]: 'Domain Setup',
  [OnboardingSection.WEBSITE]: 'Website Integration',
  [OnboardingSection.GOOGLE_BUSINESS]: 'Google My Business',
  [OnboardingSection.AGGREGATORS]: 'Order Aggregators',
  [OnboardingSection.PRINTER]: 'Printer Setup',
};

const OnboardingLayout: React.FC<OnboardingLayoutProps> = ({
  children,
  activeSection,
  onSectionChange,
}) => {
  const currentSectionTitle = SECTION_TITLES[activeSection as OnboardingSection] || 'Setup';

  return (
    <SidebarProvider defaultOpen={true}>
      <OnboardingSidebar 
        activeSection={activeSection}
        onSectionChange={onSectionChange}
      />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem className="hidden md:block">
                  <BreadcrumbLink href="#" onClick={(e) => e.preventDefault()}>
                    Onboarding
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator className="hidden md:block" />
                <BreadcrumbItem>
                  <BreadcrumbPage>{currentSectionTitle}</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>
        </header>
        <div className="flex flex-1 flex-col gap-4 p-4 pt-0">
          {children}
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
};

export default OnboardingLayout;